import React, { useState, useEffect } from "react";
import { Icon } from "@foris/avocado-icons";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Select, SkeletonBase } from "@foris/avocado-suite";
import { useApolloClient } from "@apollo/client";
import { QUERY_SELECT_COMPONENT } from "./graphql/changeComponent.queries";
import { CourseComponentPage, CourseComponent } from "../../../../models/ISchema";
import { IParams } from "../../../../models/IParams";
import css from "./searchComponent.module.scss";

const FIRST_SEARCH = 20;

interface ISearchComponentProps {
  courseId: any;
  sendValues: any;
  initValue: any;
  onDisabled?: (isDisabled: boolean) => void;
}

const SearchComponent: React.FC<ISearchComponentProps> = ({
  courseId,
  onDisabled,
  ...props
}: ISearchComponentProps) => {
  const { sendValues, initValue } = props;
  const client = useApolloClient();
  const { scenario }: IParams = useParams();
  const { t } = useTranslation();
  const [options, setOptions] = useState(null);
  const [selectValue, setSelectValue] = useState(initValue);
  const [loading, setLoading] = useState(false);

  const isDisabled = initValue && options?.length === 1;

  const serializeOptions = (component: CourseComponentPage) => {
    const CourseComponentOptions = [];
    component.items.forEach((optionComponent: CourseComponent) => {
      CourseComponentOptions.push({
        label: (
          <div className={css.container_contentSelect}>
            {optionComponent.component.name}
            {optionComponent.id === selectValue?.value && (
              <Icon className={css.icon} name="check" color="neutral-70" />
            )}
          </div>
        ),
        value: optionComponent.id,
        ogLabel: optionComponent.component.name,
      });
    });
    return CourseComponentOptions;
  };

  useEffect(() => {
    const queryOptions = () => {
      return {
        query: QUERY_SELECT_COMPONENT,
        variables: {
          scenarioId: scenario,
          filter: {
            fields: {
              courseId: {
                eq: courseId,
              },
            },
            pagination: {
              page: 1,
              size: FIRST_SEARCH,
            },
          },
        },
      };
    };

    const getDataOptions = async () => {
      try {
        setLoading(true);
        const query = queryOptions();
        const { data } = await client.query(query);
        const comOption = serializeOptions(data.data.courseComponents);
        setOptions(comOption);
        setLoading(false);
      } catch (error) {
        setOptions([]);
        setLoading(false);
      }
    };
    const getCacheDataOptions = async () => {
      try {
        setLoading(true);
        const query = queryOptions();
        const data = await client.readQuery(query);
        const comOption = serializeOptions(data.data.courseComponents);
        setOptions(comOption);
        setLoading(false);
      } catch (error) {
        getDataOptions();
      }
    };

    if (options === null) getCacheDataOptions();
  }, [client, scenario, courseId, options]);

  useEffect(() => {
    onDisabled?.(isDisabled);
  }, [isDisabled]);

  return (
    <div className={css.container}>
      {!loading ? (
        <Select
          label={t("calendar-view.option-select.label")}
          disabled={isDisabled}
          isClearable
          isLoading={loading}
          className={css.container_selectValidate}
          value={
            selectValue
              ? {
                  label: selectValue?.ogLabel || selectValue?.label,
                  value: selectValue?.value,
                }
              : null
          }
          placeholder={t("calendar-view.option-select.placeholder")}
          options={options}
          onChange={(value: any) => {
            setSelectValue(value);
            sendValues(value);
          }}
        />
      ) : (
        <SkeletonBase width="100%" height={36} maxWidth="314px" />
      )}
    </div>
  );
};

export default SearchComponent;
